// Nadrama <https://nadrama.com>
// Copyright Nadrama Pty Ltd
// All rights reserved.
import { apiBaseUrl } from "../api/config";
import { navigateExternal } from "../external-navigation";
import { createProof } from "./crypto";
import { markSessionExpired } from "./state";
import { clearPendingSlot, clearSlot, createPendingSlot, getPendingSlot, getSlot, promotePendingSlot, updateSlot, type SlotRecord } from "./storage";

export type AuthConfig = { issuer: string; pushedAuthorizationRequestEndpoint: string; tokenEndpoint: string; revocationEndpoint: string };
type AuthResult = { redirect: string; accessTokenHash: string; expiresAt: string };
class AuthHTTPError extends Error { constructor(readonly status: number) { super(`Authentication failed (${status}).`); } }
const authFetch = (path: string, init?: RequestInit) => fetch(new URL(path, apiBaseUrl), { credentials: "include", ...init, headers: { Accept: "application/json", "X-Nadrama-CSRF": "1", ...(init?.body ? { "Content-Type": "application/json" } : {}), ...init?.headers } });
const json = async <T>(response: Response): Promise<T> => { if (!response.ok) throw new AuthHTTPError(response.status); return response.json() as Promise<T>; };
export const parseAuthConfig = (text: string): AuthConfig | undefined => {
  try {
    const value = JSON.parse(text) as Record<string, unknown>;
    const fields = ["issuer", "pushedAuthorizationRequestEndpoint", "tokenEndpoint", "revocationEndpoint"] as const;
    if (!fields.every((field) => typeof value[field] === "string" && value[field] !== "")) return undefined;
    if (!fields.every((field) => ["http:", "https:"].includes(new URL(value[field] as string).protocol))) return undefined;
    return value as AuthConfig;
  } catch {
    return undefined;
  }
};
const inlineAuthConfig = (): AuthConfig | undefined => {
  if (typeof document === "undefined") return undefined;
  const text = document.getElementById("nadrama-auth-config")?.textContent;
  return text ? parseAuthConfig(text) : undefined;
};
export const getAuthConfig = async (): Promise<AuthConfig> => inlineAuthConfig() ?? json(await authFetch("/auth/config"));
export const hasAuthSlot = async (slot: number): Promise<boolean> => Boolean((await getSlot(slot))?.accessTokenHash);
const proofFor = (record: SlotRecord, method: string, url: string, ath?: string) => createProof(record.privateKey, record.jwk, method, url, ath);

export const startLogin = async (slot = 0, destination = "/select", prompt?: "create"): Promise<void> => {
  const record = await createPendingSlot(slot);
  try {
    const config = await getAuthConfig();
    const dpop = await proofFor(record, "POST", config.pushedAuthorizationRequestEndpoint);
    const result = await json<{ redirect: string }>(await authFetch("/auth/par", { method: "POST", headers: { DPoP: dpop }, body: JSON.stringify({ slot, destination, dpopJkt: record.jkt, ...(prompt ? { prompt } : {}) }) }));
    navigateExternal(result.redirect);
  } catch (error) {
    await clearPendingSlot(slot);
    throw error;
  }
};

export const completeLogin = async (slot: number): Promise<string> => {
  const record = await getPendingSlot(slot);
  if (!record) throw new Error("This login key is missing. Restart sign in in this browser.");
  const config = await getAuthConfig();
  const result = await json<AuthResult>(await authFetch("/auth/token", { method: "POST", headers: { DPoP: await proofFor(record, "POST", config.tokenEndpoint), "X-Nadrama-Auth-Slot": String(slot) } }));
  try { await promotePendingSlot(record, result.accessTokenHash, result.expiresAt); }
  catch (error) { await clearRemote(slot); await clearSlot(slot); throw error; }
  return result.redirect;
};

const pending = new Map<number, Promise<void>>();
export const refreshSlot = (slot: number, previousHash?: string): Promise<void> => {
  const existing = pending.get(slot); if (existing) return existing;
  const run = async () => {
    const refresh = async () => {
      const record = await getSlot(slot);
      if (!record) {
        markSessionExpired(slot);
        throw new Error("Authentication key is missing. Sign in again.");
      }
      if (previousHash && record.accessTokenHash !== previousHash) return;
      const config = await getAuthConfig();
      try {
        const result = await json<Omit<AuthResult, "redirect">>(await authFetch("/auth/refresh", { method: "POST", headers: { DPoP: await proofFor(record, "POST", config.tokenEndpoint), "X-Nadrama-Auth-Slot": String(slot) } }));
        await updateSlot(record, result.accessTokenHash, result.expiresAt);
      } catch (error) {
        if (error instanceof AuthHTTPError && error.status === 409) {
          const current = await getSlot(slot);
          if (previousHash && current?.accessTokenHash !== previousHash) return;
        }
        if (error instanceof AuthHTTPError && error.status === 401) {
          await clearRemote(slot);
          await clearSlot(slot);
          markSessionExpired(slot);
        }
        throw error;
      }
    };
    const locks = navigator.locks;
    if (locks) await locks.request(`nadrama-auth-${slot}`, refresh); else await refresh();
  };
  const promise = run().finally(() => pending.delete(slot)); pending.set(slot, promise); return promise;
};

export const signRequest = async (request: Request): Promise<{ request: Request; hash?: string }> => {
  const value = request.headers.get("X-Nadrama-Auth-Slot");
  if (value === null) return { request };
  const slot = Number(value); const record = await getSlot(slot);
  if (!record?.accessTokenHash) {
    markSessionExpired(slot);
    throw new Error("Authentication credentials are missing. Sign in again.");
  }
  const headers = new Headers(request.headers); headers.delete("Authorization");
  headers.set("DPoP", await proofFor(record, request.method, request.url, record.accessTokenHash));
  return { request: new Request(request, { headers }), hash: record.accessTokenHash };
};

export const authenticatedFetch: typeof fetch = async (input, init) => {
  const original = new Request(input, init);
  const slotValue = original.headers.get("X-Nadrama-Auth-Slot");
  const slot = slotValue === null ? undefined : Number(slotValue);
  if (slot !== undefined) {
    const record = await getSlot(slot);
    if (record?.expiresAt && Date.parse(record.expiresAt) <= Date.now() + 15_000) await refreshSlot(slot, record.accessTokenHash);
  }
  const signed = await signRequest(original);
  const response = await fetch(signed.request);
  if (response.status !== 401 || slotValue === null) return response;
  try { await refreshSlot(Number(original.headers.get("X-Nadrama-Auth-Slot")), signed.hash); } catch { return response; }
  if (!["GET", "HEAD"].includes(original.method.toUpperCase())) return response;
  const retried = await fetch((await signRequest(original)).request);
  if (retried.status === 401) markSessionExpired(slot!);
  return retried;
};

const clearRemote = async (slot: number): Promise<void> => {
  try { await authFetch("/auth/clear", { method: "POST", headers: { "X-Nadrama-Auth-Slot": String(slot) } }); } catch { /* Local key removal still makes copied cookies unusable. */ }
};

export const logout = async (slot: number): Promise<string> => {
  const operation = async () => {
    let redirect = "/";
    try {
      const record = await getSlot(slot);
      if (record) { const config = await getAuthConfig(); const response = await authFetch("/auth/revoke", { method: "POST", headers: { DPoP: await proofFor(record, "POST", config.revocationEndpoint), "X-Nadrama-Auth-Slot": String(slot) } }); if (response.ok) redirect = (await response.json() as { redirect: string }).redirect; }
      else await clearRemote(slot);
    } catch {
      await clearRemote(slot);
    } finally { await clearSlot(slot); }
    return redirect;
  };
  if (navigator.locks) return navigator.locks.request(`nadrama-auth-${slot}`, operation);
  try { await pending.get(slot); } catch { /* Logout must continue after a failed refresh. */ }
  return operation();
};
